// app/src/lib/delivery/model.js — delivery-platform statement domain model.
//
// A "statement" is a MONTHLY summary the merchant downloads from a delivery
// platform's portal and uploads as a PDF. We OCR it into a small set of
// CANONICAL money buckets (cents) so DoorDash / Uber Eats / Grubhub can be
// compared on the same axes regardless of each portal's idiosyncratic labels.
//
// All money is integer minor units (cents). Gross sales is the customer-facing
// food+beverage subtotal BEFORE the platform's deductions; net payout is what
// actually lands in the bank after every deduction.

/** @typedef {'doordash'|'ubereats'|'grubhub'} Platform */

/** Canonical platforms we recognize, with display metadata. */
export const PLATFORMS = Object.freeze({
  doordash: { id: 'doordash', label: 'DoorDash', color: '#FF3008' },
  ubereats: { id: 'ubereats', label: 'Uber Eats', color: '#06C167' },
  grubhub:  { id: 'grubhub',  label: 'Grubhub',  color: '#FF8000' },
});

export const PLATFORM_IDS = Object.freeze(Object.keys(PLATFORMS));

/**
 * Canonical money buckets on a statement. `commission`, `marketing`, and `fees`
 * are platform DEDUCTIONS (positive = money kept by the platform). `tax` is a
 * pass-through the platform collects and (usually) remits. `netPayout` is the
 * deposit to the bank.
 * @typedef {Object} StatementBuckets
 * @property {number} grossSalesCents   - food+bev subtotal before deductions
 * @property {number} commissionCents   - marketplace / commission fee
 * @property {number} marketingCents    - ads, sponsored listings, promotions
 * @property {number} feesCents          - processing + misc deductions/adjustments
 * @property {number} taxCents           - sales tax collected (pass-through)
 * @property {number} tipsCents          - customer tips collected (pass-through, employee-due)
 * @property {number} otherCents         - error charges, adjustments, amendments, refunds, misc
 * @property {number} netPayoutCents     - deposited to bank after all deductions
 */

/** The cents fields a statement record carries (all default 0). */
export const BUCKET_KEYS = Object.freeze([
  'grossSalesCents', 'commissionCents', 'marketingCents', 'promotionsCents', 'feesCents', 'taxCents', 'tipsCents', 'otherCents', 'netPayoutCents',
]);

/**
 * Categorize a single statement line by its label. Platforms use wildly
 * different wording, so this maps each leaf line to exactly ONE canonical
 * bucket. Order matters — more specific patterns first ("marketing fee" must
 * be marketing, not fees; "sales tax" must be tax, not fees).
 * @returns {'commission'|'marketing'|'promotions'|'fees'|'tax'|'tips'|'other'}
 */
export function categorizeLine(label) {
  const s = String(label || '').toLowerCase();
  if (/(tip|gratuity)/.test(s)) return 'tips';
  if (/\btax(es)?\b|sales tax/.test(s)) return 'tax';
  if (/(commission|marketplace)/.test(s)) return 'commission';
  // Customer/container fees are charged to the diner and passed to the merchant
  // (money IN) — they are NOT a platform charge, so keep them out of "fees".
  if (/(customer|container)\s*fees?/.test(s)) return 'other';
  // Restaurant-funded promotions / discounts / offers — kept distinct from the
  // platform's own marketing/ad fees (operators group them as "marketing +
  // promotions" but want to see the split).
  if (/(redemption|promo|\boffers?\b|discount|funded)/.test(s)) return 'promotions';
  if (/(marketing|advertis|\bads?\b|sponsor|credit|contribution)/.test(s)) return 'marketing';
  if (/(processing|service fee|merchant fee|deliver|\bfee\b|\bfees\b|logistics)/.test(s)) return 'fees';
  if (/(error charge|adjustment|amendment|refund|chargeback|cancel|withhold|reversal)/.test(s)) return 'other';
  return 'other';
}

/** Deduction categories (their bucket totals are normally negative — money out). */
export const DEDUCTION_CATS = Object.freeze(['commission', 'marketing', 'promotions', 'fees', 'other']);

/** Map an arbitrary platform string ("Uber Eats", "DOORDASH") → a Platform id, or null. */
export function normalizePlatform(raw) {
  const s = String(raw || '').toLowerCase().replace(/[^a-z]/g, '');
  if (!s) return null;
  if (s.includes('doordash') || s === 'dd') return 'doordash';
  if (s.includes('ubereats') || s.includes('uber')) return 'ubereats';
  if (s.includes('grubhub') || s.includes('seamless')) return 'grubhub'; // Seamless = Grubhub
  return null;
}

/** Human label for a platform id, falling back to the raw value. */
export function platformLabel(id) {
  return PLATFORMS[id]?.label || (id ? String(id) : 'Unknown');
}

/**
 * Total amount the platform kept (the "take"), as a POSITIVE magnitude.
 * Bucket totals are signed (deductions are negative), so the take is the
 * negated sum of the deduction buckets. Tax/tips are pass-throughs, excluded.
 * @param {StatementBuckets} b
 */
export function takeCents(b) {
  return -((b.commissionCents || 0) + (b.marketingCents || 0) + (b.promotionsCents || 0) + (b.feesCents || 0) + (b.otherCents || 0));
}

/**
 * Effective take rate as a fraction of gross sales (0..1), or null when gross is 0.
 * @param {StatementBuckets} b
 */
export function takeRate(b) {
  const g = b.grossSalesCents || 0;
  return g > 0 ? takeCents(b) / g : null;
}

/**
 * Total cost of the channel as a fraction of sales: (sales − net payout) / sales.
 * This is the headline operators watch (target < ~30%). Unlike takeRate it's
 * derived purely from the two bottom-line numbers, so it's robust to however a
 * platform itemizes its charges. Null when gross is 0.
 * @param {StatementBuckets} b
 */
export function costRatio(b) {
  const g = b.grossSalesCents || 0;
  return g > 0 ? (g - (b.netPayoutCents || 0)) / g : null;
}

/** Default cost-ratio target operators aim to stay under. */
export const COST_TARGET = 0.30;

/**
 * Reconciliation residual (cents): how far the captured lines are from the
 * stated net payout. The invariant every statement obeys is:
 *
 *   netPayout == grossSales + Σ(every signed line)
 *
 * and each line lands in exactly one bucket, so the sum of all non-gross,
 * non-payout buckets IS that line sum. This is category-agnostic, which is why
 * it holds across platforms regardless of how each treats tax (DoorDash: none;
 * Grubhub: a separate note, not a payout line; Uber: tax collected flows in via
 * earnings and out via the marketplace-facilitator-tax amendment — the small
 * net remains in the payout). A residual near 0 means every line was captured.
 * @param {StatementBuckets} b
 */
export function reconcileResidualCents(b) {
  const lineSum = (b.commissionCents || 0) + (b.marketingCents || 0) + (b.promotionsCents || 0)
    + (b.feesCents || 0) + (b.otherCents || 0) + (b.tipsCents || 0) + (b.taxCents || 0);
  return (b.netPayoutCents || 0) - ((b.grossSalesCents || 0) + lineSum);
}

/**
 * Group the (signed) buckets into the columns operators reconcile a month by:
 *   sales + tips + feesComm + mktgPromo + adjustments + tax = payments  (signed)
 * feesComm/mktgPromo are normally negative (money out); tips are positive (passed
 * through to staff); adjustments nets customer fees + error charges/refunds.
 * @param {StatementBuckets} b
 */
export function reportColumns(b) {
  return {
    salesCents: b.grossSalesCents || 0,
    feesCommCents: (b.commissionCents || 0) + (b.feesCents || 0),
    mktgPromoCents: (b.marketingCents || 0) + (b.promotionsCents || 0),
    adjustmentsCents: b.otherCents || 0,
    tipsCents: b.tipsCents || 0,
    taxCents: b.taxCents || 0,
    paymentsCents: b.netPayoutCents || 0,
  };
}
